import React, { useRef } from "react";
import { Row, Button as AntdButton, Form, Input, Col, message, Table } from "antd";
import PropTypes from 'prop-types';
import { useSelector } from "react-redux";
import moment from 'moment';
import {
    Image as ImageIcon,
    FileText,
    FileSpreadsheet,
    Presentation,
    FileVideo,
    FileAudio,
    FileArchive,
    File as FileIcon,
} from 'lucide-react';
import FeatherIcon from 'feather-icons-react';
import { UploadOutlined } from '@ant-design/icons';
import { UploadUserTag } from './UserCardCmnts'
import Heading from '../../../../components/heading/heading';
import { MultiFileUpload } from '../../../../Api/Services/CommonApiService'

const C = {
    teal: "#0d9488", tealSoft: "#ccfbf1",
    rose: "#e11d48", roseSoft: "#ffe4e6",
    accent: "#4183f1", accentSoft: "#e3edff",
    green: "#16a34a", greenSoft: "#dcfce7",
    orange: "#ea580c", orangeSoft: "#ffedd5",
    violet: "#7c3aed", violetSoft: "#ede9fe",
    blue: "#2563eb", blueSoft: "#dbeafe",
    amber: "#d97706", amberSoft: "#fef3c7",
    inkMuted: "#6b7280", sunken: "#f3f4f6",
};

const getFileExt = (filename = "") => {
    const idx = filename.lastIndexOf(".");
    return idx > -1 ? filename.slice(idx + 1).toLowerCase() : "";
};

function fileTypeMeta(ext) {
    const image = ["png", "jpg", "jpeg", "gif", "svg", "webp", "bmp", "ico", "avif"];
    const doc = ["doc", "docx", "txt", "rtf"];
    const sheet = ["xls", "xlsx", "csv"];
    const pdf = ["pdf"];
    const video = ["mp4", "webm", "mov"];
    const archive = ["zip", "rar", "7z"];
    const presentation = ["ppt", "pptx"];
    const audio = ["mp3", "wav", "aac", "ogg", "m4a"];
    if (image.includes(ext)) {
        return { label: "Image", icon: ImageIcon, color: C.teal, soft: C.tealSoft };
    }
    if (pdf.includes(ext)) {
        return { label: "PDF", icon: FileText, color: C.rose, soft: C.roseSoft };
    }
    if (doc.includes(ext)) {
        return { label: "Document", icon: FileText, color: C.accent, soft: C.accentSoft };
    }
    if (sheet.includes(ext)) {
        return { label: "Spreadsheet", icon: FileSpreadsheet, color: C.green, soft: C.greenSoft };
    }
    if (presentation.includes(ext)) {
        return { label: "Presentation", icon: Presentation, color: C.orange, soft: C.orangeSoft };
    }
    if (video.includes(ext)) {
        return { label: "Video", icon: FileVideo, color: C.violet, soft: C.violetSoft };
    }
    if (audio.includes(ext)) {
        return { label: "Audio", icon: FileAudio, color: C.blue, soft: C.blueSoft };
    }
    if (archive.includes(ext)) {
        return { label: "Archive", icon: FileArchive, color: C.amber, soft: C.amberSoft };
    }
    return {
        label: ext ? ext.toUpperCase() : "File",
        icon: FileIcon,
        color: C.inkMuted,
        soft: C.sunken,
    };
}

const ALLOWED_EXTENSIONS = ['png', 'jpg', 'jpeg', 'pdf', 'csv', 'xls', 'xlsx'];
function convertAndFormatDateTime2(dateTime, company, profile) {
    if (!dateTime || !company?.timeZone || !profile?.timeZone) return "";
    const extractTimezoneName = (tzStr) => {
        const match = tzStr.match(/\)\s*([A-Za-z/_]+)/);
        return match ? match[1] : "";
    };
    const fromTimezone = extractTimezoneName(company?.timeZone);
    const toTimezone = extractTimezoneName(profile?.timeZone);
    if (!moment.tz.zone(fromTimezone) || !moment.tz.zone(toTimezone)) {
        console.warn("Invalid timezone provided");
        return "";
    }
    const converted = moment.tz(dateTime, fromTimezone).tz(toTimezone)
    const dateFormat = profile?.dateFormate && profile?.dateFormate !== "" ? profile.dateFormate : "YYYY-MM-DD";
    const formattedDate = converted.format(dateFormat);

    return formattedDate
}
const RelatedDocUpload = ({ edit, FILEList, setFileList, form }) => {
    const localData = useSelector((state) => state.localRedux);
    const ProfileData = localData.profile
    const profile = ProfileData
    const companyProfile = localData.company
    const MAX_FILE_SIZE = Number(process.env.REACT_APP_MAX_FILE_SIZE) // 20 MB
    const fileInputRef = useRef(null);
    const removeFile = async (index) => {
        const updatedFormData = [...FILEList];
        updatedFormData.splice(index, 1);
        setFileList(updatedFormData);

        // Remove stale field from form
        const fieldName = `title-${index}`;
        form.setFieldsValue({ [fieldName]: undefined });

        // Optional: clean up all dynamic fields if needed
        const newFields = {};
        updatedFormData.forEach((file, i) => {
            newFields[`title-${i}`] = file.title;
        });
        form.setFieldsValue(newFields);
    };
    const handleFileListChange = async (index, fieldName, value) => {
        const updatedFormData = [...FILEList];
        updatedFormData[index] = {
            ...updatedFormData[index],
            [fieldName]: value,
        };
        setFileList(updatedFormData);
    };
    const FilesColumns = [
        // {
        //     title: 'Title',
        //     dataIndex: 'title',
        //     key: 'title',
        //     render: (text, record, index) => (
        //         <Form.Item name={`title-${index}`} initialValue={record.title} rules={[{ required: true, message: 'Title is required' }]} >
        //             {edit ? <Input
        //                 name="title"
        //                 value={record.title}
        //                 onChange={(e) =>
        //                     handleFileListChange(index, 'title', e.target.value)
        //                 }
        //                 placeholder="Enter Title" />
        //                 : <span >
        //                     {record.title}
        //                 </span>}
        //         </Form.Item>
        //     ),
        //     width: '25%',
        // },
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            render: (text, record, index) => (
                <Form.Item
                    name={`title-${index}`}
                    initialValue={record.title}
                    rules={[{
                        required: true,
                        transform: (value) => value?.trim(), message: 'Title is required'
                    }]}
                >
                    {edit ? (
                        <Input
                            value={record.title}
                            onChange={(e) =>
                                handleFileListChange(index, 'title', e.target.value)
                            }
                            placeholder="Enter Title"
                        />
                    ) : (
                        <span>{record.title}</span>
                    )}
                </Form.Item>
            ),
            width: '25%',
        },
        {
            title: "File Name",
            dataIndex: 'fileName',
            key: 'fileName',
            render: (text, record) => (
                <Form.Item>
                    <span>
                        {record.fileName}
                    </span>
                </Form.Item>
            ),
            width: '20%',
        },
        {
            title: "Type",
            dataIndex: 'type',
            key: 'type',
            width: '10%',
            render: (text, record) => (
                <Form.Item><span >{record.type}</span></Form.Item>
            ),
        },
        {
            title: "Uploaded By",
            dataIndex: 'uploadedBy',
            key: 'uploadedBy',
            width: '25%',
            render: (text, record) => (
                <Form.Item>
                    <UploadUserTag record={record} />
                </Form.Item>
            ),
        },
        {
            title: "Uploaded Date",
            dataIndex: 'uploadedDate',
            key: 'uploadedDate',
            width: '15%',
            render: (text, record) => (
                <Form.Item><span> {convertAndFormatDateTime2(record?.uploadedDate, companyProfile, profile)}</span></Form.Item>
            )
        },
        {
            title: '',
            dataIndex: 'download',
            key: 'download',
            width: '10%',
            render: (text, record) => (
                <Form.Item>
                    <a title="Download" href={record.url} download={record.fileName}> <FeatherIcon icon="download-cloud" size={25} /> </a>
                </Form.Item>
            ),
        },
        {
            title: '',
            dataIndex: 'action',
            key: 'action',
            width: '5%',
            render: (text, record, index) => {
                if (edit && FILEList.length > 0) {
                    return (<span title="Delete">
                        <FeatherIcon icon="trash-2" size={20} style={{ margin: "0px" }} className="fetcherDelete-Icon"
                            onClick={() => removeFile(index)}
                        /></span>
                    );
                }
            },
        },
    ];
    function getCurrentDateTimeFromZoneString(tzStr) {
        if (!tzStr) return "";
        const extractTimezoneName = (tzStr) => {
            const match = tzStr.match(/\)\s*([A-Za-z/_]+)/);
            return match ? match[1] : "";
        };
        const timezone = extractTimezoneName(tzStr)
        if (!moment.tz.zone(timezone)) {
            console.warn("Invalid timezone:", timezone);
            return "";
        }
        return moment.tz(timezone).format("YYYY-MM-DD HH:mm");
    }
    const handlefileuploadChange = async (e) => {
        let hideMessage;
        const files = Array.from(e.target.files);
        const formData = new FormData();
        files.forEach(file => {
            formData.append('files', file);
        });
        try {
            hideMessage = message.loading("Loading...", 0);
            const response = await MultiFileUpload(formData);
            if (response.status === 201) {
                const updatedFileList = response.data.map((fileItem) => {
                    const formattedDateTime = getCurrentDateTimeFromZoneString(companyProfile?.timeZone);
                    const Filename = fileItem.mediaName.replace(/^files-\d+\./, '');
                    const lastDotIndex = fileItem.mediaName.lastIndexOf('.');
                    const extensionWithDot = fileItem.mediaName.slice(lastDotIndex + 1);
                    return {
                        title: undefined,
                        fileName: Filename,
                        type: extensionWithDot,
                        uploadedById: profile.userId,
                        uploadedBy: `${profile.firstName} ${profile.lastName}`,
                        uploadedProfile: profile.profilePic,
                        uploadedDate: formattedDateTime,
                        url: fileItem.mediaUrl,
                    };
                });
                hideMessage();
                setFileList(prevFileList => [...prevFileList, ...updatedFileList]);
            }
        } catch (error) {
            hideMessage();
            message.error(error);
        }
    };
    const handleFileSelection = (e) => {
        const files = Array.from(e.target.files);
        const invalidFiles = files.filter((file) => {
            const extension = file.name.split('.').pop().toLowerCase();
            return !ALLOWED_EXTENSIONS.includes(extension);
        });
        if (invalidFiles.length > 0) {
            message.error(
                'Only PNG, JPG, JPEG, PDF, CSV, XLS and XLSX files are allowed.'
            );
            e.target.value = '';
            return;
        }
        if (files.length > 5) {
            message.warning('You can upload a maximum of 5 files at a time');
            e.target.value = '';
            return;
        }
        if (files.some((file) => file.size > MAX_FILE_SIZE)) {
            message.error(`One or more files exceed the maximum size.`);
            e.target.value = '';
            return;
        }
        handlefileuploadChange(e);
    };
    return (
        <>
            <div style={{ display: "flex", alignItems: "center", gap: "50px" }}>
                <Heading as="h5">Related Documents</Heading>
                {edit &&
                    <div>
                        <input
                            type="file"
                            name="files"
                            multiple
                            ref={fileInputRef}
                            accept=".png,.jpg,.jpeg,.pdf,.csv,.xls,.xlsx"
                            style={{ display: 'none' }}
                            onChange={handleFileSelection}
                        // onChange={(e) => {
                        //     const files = Array.from(e.target.files);
                        //     if (files.length > 5) {
                        //         message.warning("You can upload a maximum of 5 files at a time");
                        //     } else if (files.some(file => file.size > MAX_FILE_SIZE)) {
                        //         message.error(`One or more files exceed the maximum size of 5 MB.`);
                        //     } else {
                        //         handlefileuploadChange(e);
                        //     }
                        // }}
                        />
                        <AntdButton icon={<UploadOutlined />} onClick={() => fileInputRef.current.click()}                                                                                        >
                            Click to Upload
                        </AntdButton>
                    </div>
                }
            </div>
            {FILEList.length > 0 ?
                <Row gutter={30} style={{ margin: "20px 0px" }}>
                    <Col span={24} style={{ marginBottom: 20 }}>
                        <div className={`table-bordered leaderboard-table table-responsive ${edit && 'FileTable'}`}>
                            <Table
                                dataSource={FILEList}
                                columns={FilesColumns}
                                rowKey={(record, index) => index.toString()}
                                pagination={false}
                            />
                        </div>
                    </Col>
                </Row> : <Row gutter={30} style={{ margin: "20px 0px" }} />
            }
        </>
    )
}
RelatedDocUpload.propTypes = {
    FILEList: PropTypes.array.isRequired,
    setFileList: PropTypes.func.isRequired,
    edit: PropTypes.bool.isRequired,
    form: PropTypes.object.isRequired,
};
const RelatedDocs = ({ file, index }) => {
    const localData = useSelector((state) => state.localRedux);
    const ProfileData = localData.profile
    const profile = ProfileData
    const companyProfile = localData.company
    const shortenFilename = (filename, maxLength = 20) => {
        const extension = filename.substring(filename.lastIndexOf('.'));
        const nameWithoutExtension = filename.substring(0, filename.lastIndexOf('.'));
        if (nameWithoutExtension.length > maxLength) {
            return `${nameWithoutExtension.substring(0, maxLength)}...${extension}`;
        }
        return filename;
    };
    const meta = fileTypeMeta(getFileExt(file.fileName));
    const FileTypeIcon = meta.icon;
    return (<div className='RightSide-Pic-DataShadow' key={index}>
        <div style={{ padding: "10px", }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                <div className='doc-title div-ellipsis'>{file?.title}</div>
            </div>
            <div className='doc-date' style={{ fontSize: "12px", marginBottom: 5 }}>Uploaded by</div>
            <div style={{ display: "flex", alignItems: "center", fontSize: "14px", minWidth: 0, overflow: "hidden"}}>
                <div className='RelatedDocs-user' style={{ flex: "0 1 auto", minWidth: 0, overflow: "hidden" }}>
                    <UploadUserTag record={file} page="ReDoc" />
                </div>
                <div style={{ flexShrink: 0, whiteSpace: "nowrap", marginLeft: 5 }}>on</div>
                <div style={{ flexShrink: 0, whiteSpace: "nowrap", marginLeft: 5, color: "black" }}>
                    {convertAndFormatDateTime2(file?.uploadedDate, companyProfile, profile)}
                </div>
            </div>
        </div>
        <div style={{ padding: "10px", borderTop: "1px solid #ddd", display: "flex", alignItems: "center", justifyContent: "space-between" }} >
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <div
                    title={meta.label}
                    style={{
                        width: 25,
                        height: 25,
                        borderRadius: 8,
                        background: meta.soft,
                        color: meta.color,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}
                >
                    <FileTypeIcon size={18} strokeWidth={1.75} />
                </div>
                <div>
                    <div title={file.fileName} style={{
                        cursor: "default",
                        fontWeight: 700,
                        fontSize: 12,
                    }}>{shortenFilename(file.fileName, 20)}</div>
                </div>
            </div>
            <div>
                <a href={file.url} download={file.fileName}> <FeatherIcon icon="download-cloud" size={20} style={{ display: "flex", alignItems: "center" }} /> </a>
            </div>
        </div>
    </div>)
}
RelatedDocs.propTypes = {
    file: PropTypes.object.isRequired,
    index: PropTypes.number.isRequired,
};

export { RelatedDocUpload, RelatedDocs }